import 'react-image-crop/dist/ReactCrop.css';

import { func, number, shape, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactCrop from 'react-image-crop';

function ImageCropper(props) {
  const { imageToCrop, onImageCropped, userDimensions } = props;

  const [cropConfig, setCropConfig] = useState(
    // default crop config
    {
      unit: '%',
      width: 30,
      aspect: userDimensions.width / userDimensions.height,
    },
  );

  const [imageRef, setImageRef] = useState();

  // func that resizes the image based on user preference
  function getResizedCanvas(canvas, newWidth, newHeight) {
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = newWidth;
    tmpCanvas.height = newHeight;

    const ctx = tmpCanvas.getContext('2d');
    ctx.drawImage(
      canvas,
      0,
      0,
      canvas.width,
      canvas.height,
      0,
      0,
      newWidth,
      newHeight,
    );

    return tmpCanvas;
  }

  function getCroppedImage(sourceImage, cropConfigInfo) {
    // creating the cropped image from the source image
    const canvas = document.createElement('canvas');
    const scaleX = sourceImage.naturalWidth / sourceImage.width;
    const scaleY = sourceImage.naturalHeight / sourceImage.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;

    canvas.width = Math.ceil(cropConfigInfo.width * pixelRatio * scaleX);
    canvas.height = Math.ceil(cropConfigInfo.height * pixelRatio * scaleY);

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      sourceImage,
      cropConfigInfo.x * scaleX,
      cropConfigInfo.y * scaleY,
      cropConfigInfo.width * scaleX,
      cropConfigInfo.height * scaleY,
      0,
      0,
      cropConfigInfo.width * scaleX,
      cropConfigInfo.height * scaleY,
    );

    const getDefinedCanvasDimensions = getResizedCanvas(
      canvas,
      userDimensions.width,
      userDimensions.height,
    );

    return new Promise((resolve, reject) => {
      getDefinedCanvasDimensions.toBlob(
        blob => {
          // returning an error
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          // creating a Object URL representing the Blob object given
          const croppedImageUrl = window.URL.createObjectURL(blob);
          resolve({ croppedImageUrl, blob });
        },
        'image/jpeg',
        1,
      );
    });
  }

  async function cropImage(crop) {
    if (imageRef && crop.width && crop.height) {
      const { croppedImageUrl, blob } = await getCroppedImage(imageRef, crop);

      // calling the props function to expose
      // croppedImage to the parent component
      onImageCropped(croppedImageUrl, blob);
    }
  }

  // this effect sets the crop area and crops the image whenever the user preference changes
  useEffect(() => {
    if (userDimensions.width && userDimensions.height) {
      setCropConfig({
        unit: '%',
        width: 30,
        aspect: userDimensions.width / userDimensions.height,
      });
      cropImage(cropConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageRef, userDimensions]);

  return (
    <ReactCrop
      src={imageToCrop}
      crop={cropConfig}
      ruleOfThirds
      onImageLoaded={imgRef => setImageRef(imgRef)}
      onComplete={cropConfigInfo => cropImage(cropConfigInfo)}
      onChange={cropConfigInfo => setCropConfig(cropConfigInfo)}
      crossorigin="anonymous" // to avoid CORS-related problems
    />
  );
}

ImageCropper.propTypes = {
  imageToCrop: string.isRequired,
  onImageCropped: func.isRequired,
  userDimensions: shape({ width: number.isRequired, height: number.isRequired })
    .isRequired,
};

export default ImageCropper;
